<script>
  // Step 4 body: Export Frames. Renders inside a <StepCard>; the parent panel
  // owns the card's number/title/subline/expanded/warning.
  //
  // This is a thin inline form over the same exporter the legacy download
  // modal uses — ListDownloadModal.svelte's downloadBundle() is the
  // reference call site. Do not open that modal or duplicate its body; call
  // downloadJobBundle() directly.
  import { getContext } from "svelte";

  import { downloadJobBundle, downloadArchive } from "#src/extensions/download-job-bundle";
  import { WBPVersion, GSVersion } from "#src/stores/ui.js";
  import { profile } from "#src/stores/auth.js";
  import { alerts } from "#src/stores/alerts.js";
  import ConfirmGsUpdate from "#src/lib/ConfirmGsUpdate.svelte";
  import { splitPreview } from "../split.js";
  import { scopeBucket } from "../scope-key.js";
  import { plural } from "../labels.js";
  import { isFrameBuilt } from "../derive.js";

  export let group;
  /** The FrameX working set, derived once by FrameXPanel. */
  export let workingIds;
  export let selectedLocationList;
  /** Every frame in the working set, derived once by FrameXPanel. */
  export let frames;

  const edgeTreatments = getContext("edgeTreatments");

  let downloading = false;
  let selectedOnly = false;
  let splitBy = "none";
  let includeData = false;
  let includeCAD = false;
  let includeWBP = false;
  let includeGS = false;

  // Mirrors the Complete Job / Selected Only radio above. The split preview,
  // the button's count and the download loop all read this, so every number
  // the step shows equals what Download actually writes.
  $: exportFrames = selectedOnly ? selectedLocationList : frames;

  // Step 4 is the one genuinely terminal step, so an empty set disables the
  // button with a reason rather than hiding or locking it. Counted within the
  // current scope, never the whole working set: under "Selected Only" with
  // three frames picked the button must read "Download (3) frames".
  $: builtCount = exportFrames.filter((loc) => isFrameBuilt($group.items[loc.record_id])).length;

  // Same rule as ListDownloadModal's disabled: nothing checked, or Glazier
  // Studio checked without the update confirmation on file — plus nothing
  // built to write out.
  $: disabled =
    builtCount === 0 ||
    (!includeData && !includeCAD && !includeWBP && !includeGS) ||
    (includeGS && !$profile?.confirmed_gs_update_on);

  // scopeOf/qtyOf are inlined here (rather than named consts) so `$group` is
  // referenced directly inside this reactive statement — that's what makes
  // Svelte re-run the preview when an item's scope/quantity changes (e.g.
  // Step 3 edits) while Step 4 is expanded, not just when the frame set or
  // split mode changes.
  $: preview = splitPreview(exportFrames, {
    splitBy,
    pageOf: (loc) => loc.page_id,
    scopeOf: (loc) => $group.items[loc.record_id]?.scope,
    qtyOf: (loc) => $group.items[loc.record_id]?.quantity ?? 0,
  });

  // Buckets `list` exactly as splitPreview counts it (via the shared
  // scopeBucket), so the calls made below match the previewed file count.
  // Pages have no human label here, so page groups are numbered in arrival
  // order — which already follows page order.
  function splitGroups(list, by) {
    const groups = [];
    const byKey = new Map();

    for (const loc of list || []) {
      const bucket = by === "page" ? null : scopeBucket($group.items[loc.record_id]?.scope);
      const key = bucket ? bucket.key : loc.page_id;

      let entry = byKey.get(key);
      if (!entry) {
        entry = { label: bucket ? bucket.title : `Page ${groups.length + 1}`, locations: [] };
        byKey.set(key, entry);
        groups.push(entry);
      }
      entry.locations.push(loc);
    }

    return groups;
  }

  // Filename/folder name for a bucket: the scope/page label, with path
  // separators collapsed to a dash and control chars dropped (filtered by code
  // point, not a control-char regex literal). splitGroups already gives blank
  // scopes the "Unassigned" label.
  function safeName(label) {
    const cleaned = String(label || "Unassigned")
      .replace(/[/\\]+/g, "-")
      .split("")
      .filter((c) => c.charCodeAt(0) >= 0x20)
      .join("")
      .trim();
    return cleaned || "Unassigned";
  }

  // Options for a collect call — mirrors downloadOne, but returns the members
  // map (collectOnly) in archive-ready shape (forArchive) instead of
  // downloading each bucket on its own.
  function collectOpts(selectedLocations) {
    return {
      productQuote: $group?.project_type === "product",
      filename: `${$group?.job?.name}`,
      includeData,
      includeWBP,
      includeCAD,
      includeGS,
      collectionsOnly: true,
      selectedLocations,
      wbpVersion: $WBPVersion,
      gsVersion: $GSVersion,
      collectOnly: true,
      forArchive: true,
    };
  }

  async function downloadOne(filename, selectedLocations) {
    await downloadJobBundle(
      $group,
      // Unused by any of the four formats this step drives — types only
      // feeds the report-PDF path (includeReportsPdf), which this panel
      // never sets.
      [],
      { edgeTreatments: $edgeTreatments },
      {
        productQuote: $group?.project_type === "product",
        filename,
        includeData,
        includeWBP,
        includeCAD,
        includeGS,
        // Frames are collections — scope the bundle to those, not every
        // item in the job.
        collectionsOnly: true,
        selectedLocations,
        wbpVersion: $WBPVersion,
        gsVersion: $GSVersion,
      },
      $profile,
    );
  }

  async function download() {
    downloading = true;

    try {
      if (splitBy === "none") {
        await downloadOne(
          `${$group?.job?.name}`,
          selectedOnly ? selectedLocationList.map((loc) => loc.id) : null,
        );
      } else {
        // One zip: a folder per scope/page, each file named after that
        // scope/page (plus its own extension), so a single browser download
        // can't drop files. A collision guard suffixes ` (2)`, ` (3)`… within a
        // folder, so two same-extension formats (e.g. WinBidPro + Glazier
        // Studio) or two labels that sanitise alike never overwrite each other;
        // different formats keep their own extensions and so never collide.
        const extOf = (name) => (name.includes(".") ? name.slice(name.lastIndexOf(".")) : "");
        const merged = {};
        for (const bucket of splitGroups(exportFrames, splitBy)) {
          const label = safeName(bucket.label);
          const members = await downloadJobBundle(
            $group,
            [],
            { edgeTreatments: $edgeTreatments },
            collectOpts(bucket.locations.map((loc) => loc.id)),
            $profile,
          );
          for (const [name, url] of Object.entries(members || {})) {
            const ext = extOf(name);
            let key = `${label}/${label}${ext}`;
            let n = 2;
            while (merged[key]) key = `${label}/${label} (${n++})${ext}`;
            merged[key] = url;
          }
        }
        await downloadArchive(`${$group?.job?.name}`, merged);
      }

      alerts.add({ type: "ok", message: "Export ready." });
    } catch (error) {
      console.error("FrameX download failed:", error);
      alerts.add({ type: "error", message: "Failed to export files." });
    }

    downloading = false;
  }
</script>

{#if workingIds.length === 0}
  <div class="fx-hint">Locate and analyze frames before exporting.</div>
{:else}
  <div class="fx-step4-form">
    <div class="fx-scope-row">
      <label class="fx-scope-radio">
        <input type="radio" bind:group={selectedOnly} value={false} />
        Complete Job
      </label>
      <label class="fx-scope-radio">
        <input type="radio" bind:group={selectedOnly} value={true} />
        Selected Only
      </label>
    </div>

    <div class="fx-field-label">Split files by</div>
    <div class="fx-scope-row">
      <label class="fx-scope-radio">
        <input type="radio" bind:group={splitBy} value="none" />
        One file
      </label>
      <label class="fx-scope-radio">
        <input type="radio" bind:group={splitBy} value="page" />
        Page
      </label>
      <label class="fx-scope-radio">
        <input type="radio" bind:group={splitBy} value="scope" />
        Scope
      </label>
    </div>
    <div class="fx-split-preview">
      {preview.files} file{plural(preview.files)} · {preview.types} type{plural(preview.types)} · {preview.qty}
      qty
    </div>

    <!-- Ordered by what a glazier reaches for first: the two estimating systems,
         then the generic formats. ConfirmGsUpdate sits directly under the card that
         triggers it rather than after the whole group. -->
    <label class="fx-format-card" class:fx-format-checked={includeWBP}>
      <input type="checkbox" bind:checked={includeWBP} />
      WinBidPro (GDS)
    </label>
    <label class="fx-format-card" class:fx-format-checked={includeGS}>
      <input type="checkbox" bind:checked={includeGS} />
      Glazier Studio / PartnerPak
    </label>

    {#if includeGS && !$profile?.confirmed_gs_update_on}
      <ConfirmGsUpdate />
    {/if}

    <label class="fx-format-card" class:fx-format-checked={includeData}>
      <input type="checkbox" bind:checked={includeData} />
      Excel
    </label>
    <label class="fx-format-card" class:fx-format-checked={includeCAD}>
      <input type="checkbox" bind:checked={includeCAD} />
      CAD
    </label>

    <button class="fx-primary-button" disabled={disabled || downloading} on:click={download}>
      <!-- No ellipsis: Export acts immediately rather than opening a dialog.
           "built" is load-bearing — the count is what is eligible to go out, not
           what has already gone out, and steps 2 and 3 report produced work, so
           a bare number here gets read the same way. -->
      {downloading
        ? "Exporting…"
        : builtCount > 0
          ? `Export (${builtCount}) built frame${plural(builtCount)}`
          : "Export frames"}
    </button>

    {#if builtCount === 0}
      <!-- Under "Selected Only" an empty count means nothing is selected, not
           that nothing is built — the frames may all be built and simply out
           of scope. Telling the user to build them would send them nowhere. -->
      <div class="fx-hint">
        {selectedOnly && exportFrames.length === 0
          ? "Select frames to export, or choose Complete Job."
          : "Build frames before exporting."}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  // .fx-primary-button is defined globally in assets/styles/framex.scss;
  // only this margin is Step 4's own.
  .fx-primary-button {
    margin-top: 2px;
  }

  .fx-step4-form {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .fx-scope-row {
    display: flex;
    gap: 14px;
    font-size: 12px;
    color: var(--fx-text-alt);
  }

  .fx-scope-radio {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
  }

  .fx-field-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--fx-text-alt);
    margin-top: 2px;
  }

  .fx-split-preview {
    font-size: 12px;
    color: var(--fx-text-secondary);
    margin-top: -3px;
  }

  .fx-format-card {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--fx-text-alt);
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    background: #fff;
    border: 1px solid var(--fx-border);

    &.fx-format-checked {
      background: var(--fx-accent-tint-alt);
      border-color: var(--fx-accent);
    }
  }
</style>
